import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'md-letter-image',
  templateUrl: './letter-image.component.html',
  styleUrls: ['./letter-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LetterImageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() name?: string;
  @Input() color?: string;
  @Input() id?: number;
  @Input() size?: number = undefined;
  @Input() badge?: number | string;
  @Input() image?: string | null;

  @HostBinding('style.background-image') backgroundImage?: string;
  // @ts-expect-error
  @HostBinding('class.transparent-background') transparentBackground: boolean;
  @HostBinding('style.width.px') hostWidth?: number;
  @HostBinding('style.height.px') hostHeight?: number;

  colorsCount = 16;
  colorNumber = 0;
  letters = '';
  /** Drives the template: true = render initials, false = the image shows. */
  showingLetters = false;
  imagesPath = environment.images_path;
  generatedId = this.random();

  /** Last image URL handed to the probe — avoids re-fetch/flicker when an unrelated input changes. */
  private probedSrc: string | null = null;
  private probe?: HTMLImageElement;

  constructor(private element: ElementRef, private cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.hostWidth = this.size;
    this.hostHeight = this.size;
  }

  ngOnChanges() {
    const id =
      this.id ||
      this.name
        ?.split('')
        .map(x => x.charCodeAt(0))
        .reduce((acc, x) => acc + x, 0) ||
      this.generatedId;

    this.size =
      this.size ||
      parseFloat(getComputedStyle(this.element.nativeElement).width) ||
      this.element.nativeElement.clientWidth;

    this.hostHeight = this.hostWidth = this.size;

    const src = this.resolveSrc();
    if (!src) {
      this.probedSrc = null;
      this.cancelProbe();
      this.showLetters(id);
      return;
    }

    if (src === this.probedSrc) {
      // Same image as before — keep whatever the probe resolved to (image or
      // fallback initials); just refresh the letter inputs if we're on them.
      if (this.showingLetters) {
        this.showLetters(id);
      }
      return;
    }

    this.probedSrc = src;
    this.backgroundImage = `url(${src})`;
    this.transparentBackground = false;
    this.showingLetters = false;

    // A CSS background image fails silently (404, blocked request, ...) and
    // would leave an empty box — probe it and fall back to initials.
    this.cancelProbe();
    const probe = new Image();
    this.probe = probe;
    probe.onload = () => {
      if (this.probe === probe) {
        this.probe = undefined;
      }
    };
    probe.onerror = () => {
      if (this.probe === probe) {
        this.probe = undefined;
        this.showLetters(id);
        this.cd.markForCheck();
      }
    };
    probe.src = src;
  }

  ngOnDestroy() {
    this.cancelProbe();
  }

  /**
   * Uploads are referenced by bare filename and served from images_path.
   * Chat avatar URLs from ms-messaging arrive absolute (they must work for
   * the native apps too), but every tenant's uploads are served by the same
   * backend — so anything under /@/backend/uploads/ is rewritten onto our
   * own images_path. Same-origin always works, where the absolute form
   * breaks on the local stack (self-signed cert) and across subdomains.
   */
  private resolveSrc(): string | null {
    if (!this.image) {
      return null;
    }
    const uploads = this.image.match(/\/@\/backend\/uploads\/(.+)$/);
    if (uploads) {
      return this.imagesPath + uploads[1];
    }
    return /^(https?:)?\/\//.test(this.image) ? this.image : this.imagesPath + this.image;
  }

  private showLetters(id: number): void {
    this.transparentBackground = true;
    this.backgroundImage = undefined;
    this.showingLetters = true;
    this.colorNumber = (id % this.colorsCount) + 1;
    this.letters = this.name ? this.getLetter(this.name) : '?';
  }

  private cancelProbe(): void {
    if (this.probe) {
      this.probe.onload = null;
      this.probe.onerror = null;
      this.probe = undefined;
    }
  }

  getLetter(str: string): string {
    const parts = str.split(' ');
    const letters = parts.reduce(
      (value: string, part: string) =>
        value + (part && part.length ? part.charAt(0) : ''),
      '',
    );

    return letters.substr(0, 2).toUpperCase();
  }

  random(): number {
    return Math.ceil(Math.random() * this.colorsCount);
  }
}
